import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import appStoreIconUrl from "../../../backend/src/core/static/core/img/apple.svg";
import playStoreIconUrl from "../../../backend/src/core/static/core/img/playstore.svg";
import type { EmbedEvent } from "../lib/api";
import type { WidgetConfig } from "../lib/config";
import { formatEventDate } from "../lib/date";
import { getModalMountPoint } from "../lib/modal-host";
import { MarkdownText } from "./markdown-text";
import { TagPill } from "./tag-pill";

const APP_STORE_URL = "https://apps.apple.com/us/app/wasa-go/id6759002771";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=pro.lesjak.gitg&hl=en";

function buildMapsUrl(title: string, full: string): string | null {
  const query = [title, full].filter(Boolean).join(" ").trim();
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function buildShareUrl(eventId: string, baseUrl: string): string {
  return new URL(`/events/${eventId}/`, baseUrl).toString();
}

function buildMailtoBody(event: EmbedEvent, shareUrl: string): string {
  const lines = [`Event: ${event.name}`, "", `When: ${formatEventDate(event)}`];
  const where = [event.address_title, event.address_full]
    .filter(Boolean)
    .join(", ");
  if (where) lines.push("", `Where: ${where}`);
  lines.push("", `Full details at: ${shareUrl}`);
  return lines.join("\n");
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

type EventModalProps = {
  event: EmbedEvent;
  config: WidgetConfig;
  onClose: () => void;
};

export function EventModal({ event, config, onClose }: EventModalProps) {
  const mapsUrl = buildMapsUrl(event.address_title, event.address_full);

  const shareUrl = buildShareUrl(event.id, config.eventDetailBaseUrl);
  const shareTitle = event.name;
  const xUrl = `https://x.com/intent/post?${new URLSearchParams({ text: shareTitle, url: shareUrl }).toString()}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?${new URLSearchParams({ u: shareUrl }).toString()}`;
  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?${new URLSearchParams({ url: shareUrl }).toString()}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(buildMailtoBody(event, shareUrl))}`;

  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function onCopy() {
    let success = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        success = true;
      }
    } catch {
      // The async API can reject inside Shadow DOM, on insecure contexts,
      // or when the document isn't focused — fall through to the legacy path.
    }
    if (!success) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = shareUrl;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        success = document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch {
        success = false;
      }
    }
    if (success) setCopied(true);
  }

  return createPortal(
    <div
      className="gitg-modal"
      role="dialog"
      aria-label={event.name}
      aria-modal="true"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <button
        aria-label="Close event details"
        className="gitg-modal-backdrop"
        onClick={onClose}
        type="button"
      />
      <section className="gitg-detail">
        <button
          aria-label="Close event details"
          className="gitg-close-button"
          onClick={onClose}
          type="button"
          ref={(el) => {
            el?.focus();
          }}
        >
          ×
        </button>

        <div className="gitg-detail-body">
          {event.image ? (
            <img
              className="gitg-detail-image"
              src={event.image}
              alt={event.name}
            />
          ) : null}

          <div className="gitg-detail-header">
            {event.event_type === "external" ? (
              <p className="gitg-external-notice">
                External event · organised by a third party
              </p>
            ) : event.event_type === "school" ? (
              <p className="gitg-external-notice">Closed school event</p>
            ) : null}
            <h3 className="gitg-detail-title">{event.name}</h3>
            {event.event_type === "external" || event.tags.length > 0 ? (
              <div className="gitg-detail-tags">
                {event.event_type === "external" ? (
                  <TagPill
                    label="External"
                    className="gitg-label-external"
                    title="Third-party event — see the description for details"
                  />
                ) : null}
                {event.tags.map((tag) => (
                  <TagPill key={tag.id} label={tag.name} />
                ))}
              </div>
            ) : null}
          </div>

          <div className="gitg-detail-section">
            <p className="gitg-detail-label">Date and time</p>
            <p className="gitg-detail-text">{formatEventDate(event)}</p>
          </div>

          <div className="gitg-detail-section">
            <div className="gitg-detail-section-header">
              <p className="gitg-detail-label">Location</p>
              {mapsUrl ? (
                <a
                  className="gitg-detail-maps-link"
                  href={mapsUrl}
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  View on Maps
                </a>
              ) : null}
            </div>
            <p className="gitg-detail-text">
              {[event.address_title, event.address_full]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>

          {event.description ? (
            <div className="gitg-detail-section">
              <p className="gitg-detail-label">Description</p>
              <p className="gitg-detail-text">
                <MarkdownText text={event.description} />
              </p>
            </div>
          ) : null}

          {event.event_link && event.event_type !== "external" ? (
            <div className="gitg-detail-section">
              <p className="gitg-event-link-notice">
                You can also use a web registration form for this event.
              </p>
              <a
                className="gitg-event-link"
                href={event.event_link}
                rel="noreferrer noopener"
                target="_blank"
              >
                Registration form
                <ArrowRightIcon />
              </a>
            </div>
          ) : null}

          {event.contact_email ? (
            <div className="gitg-detail-section">
              <p className="gitg-detail-label">Contact</p>
              <p className="gitg-detail-text">
                {event.contact_name ? `${event.contact_name} · ` : null}
                <a
                  className="gitg-detail-contact-link"
                  href={`mailto:${event.contact_email}`}
                >
                  {event.contact_email}
                </a>
              </p>
            </div>
          ) : null}

          <div className="gitg-detail-section">
            <p className="gitg-detail-label">Share</p>
            <div className="gitg-share-buttons">
              <a
                aria-label="Share on X"
                className="gitg-share-button"
                href={xUrl}
                rel="noreferrer noopener"
                target="_blank"
              >
                <XIcon />
              </a>
              <a
                aria-label="Share on Facebook"
                className="gitg-share-button"
                href={fbUrl}
                rel="noreferrer noopener"
                target="_blank"
              >
                <FacebookIcon />
              </a>
              <a
                aria-label="Share on LinkedIn"
                className="gitg-share-button"
                href={liUrl}
                rel="noreferrer noopener"
                target="_blank"
              >
                <LinkedInIcon />
              </a>
              <a
                aria-label="Share via email"
                className="gitg-share-button"
                href={mailtoUrl}
              >
                <MailIcon />
              </a>
              <button
                aria-label={copied ? "Link copied" : "Copy link"}
                className={
                  copied
                    ? "gitg-share-button gitg-share-button-copied"
                    : "gitg-share-button"
                }
                onClick={onCopy}
                type="button"
              >
                {copied ? <CheckIcon /> : <LinkIcon />}
              </button>
            </div>
          </div>
        </div>

        {/* Hollow = handled elsewhere; pinned below the scrolling body. */}
        {event.event_link && event.event_type === "external" ? (
          <div className="gitg-detail-footer">
            <a
              className="gitg-event-link gitg-event-link-external"
              href={event.event_link}
              rel="noreferrer noopener"
              target="_blank"
            >
              Visit organiser's site
              <ExternalLinkIcon />
            </a>
          </div>
        ) : null}

        {/* Only internal events take app registrations: external events
            register with the organiser, school events with the school. */}
        {config.showAppLinks && event.event_type === "internal" ? (
          <div className="gitg-register gitg-detail-footer">
            <p className="gitg-register-label">
              Register via WASA Go mobile app
            </p>
            <div className="gitg-store-links">
              <a
                className="gitg-store-link"
                href={APP_STORE_URL}
                rel="noreferrer noopener"
                target="_blank"
              >
                <img
                  alt=""
                  aria-hidden="true"
                  className="gitg-store-icon"
                  src={appStoreIconUrl}
                />
                <span className="gitg-store-copy">
                  <span className="gitg-store-eyebrow">Download on the</span>
                  <span className="gitg-store-name">App Store</span>
                </span>
              </a>
              <a
                className="gitg-store-link"
                href={PLAY_STORE_URL}
                rel="noreferrer noopener"
                target="_blank"
              >
                <img
                  alt=""
                  aria-hidden="true"
                  className="gitg-store-icon"
                  src={playStoreIconUrl}
                />
                <span className="gitg-store-copy">
                  <span className="gitg-store-eyebrow">Get it on</span>
                  <span className="gitg-store-name">Google Play</span>
                </span>
              </a>
            </div>
          </div>
        ) : null}
      </section>
    </div>,
    getModalMountPoint(config),
  );
}
